import { wealthChart, balanceBars, shortMoney, SCENARIO_LABELS } from '../../common/stayorgo';

/**
 * "Stay or go?" on Voucha: the full result. The two paths over time with the naira's three paths as a band,
 * each path's numbers, what you would own and owe at the end, what the move costs, and every assumption.
 * Amounts can be shown in the destination's currency or in naira (at today's rate, so still today's money).
 */
const SHORT = {
  CA: 'Canada',
  GB: 'the UK',
  US: 'the US',
  IE: 'Ireland',
  DE: 'Germany',
  NL: 'the Netherlands'
};
const VIEW_TEXT = {
  stronger: 'if the naira gets stronger',
  same: 'if the naira holds its value',
  weaker: 'if the naira keeps weakening'
};
const ui = {
  naira: false
};
const full = (amount, currency) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0
    }).format(amount);
  } catch (e) {
    return currency + ' ' + Math.round(amount).toLocaleString();
  }
};
const round = v => {
  const step = Math.abs(v) >= 1e5 ? 1000 : 100;
  return Math.round(v / step) * step || 0; // never "-0"
};
export function stayResult(s) {
  const there = SHORT[s.country.code] || s.country.name;
  const rate = ui.naira ? s.fx.rate : 1;
  const cur = ui.naira ? 'NGN' : s.country.currency;
  const conv = v => v * rate;
  const nairaText = n => {
    const a = Math.abs(n);
    const t = a >= 1e6 ? (Math.round(a / 1e5) / 10).toLocaleString() + 'm' : (Math.round(a / 1000) * 1000).toLocaleString();
    return (n < 0 && t !== '0' ? '−' : '') + '₦' + t;
  };
  const money = v => ui.naira ? nairaText(v * rate) : full(round(v), cur);
  const scen = s.scenarios.map(x => ({
    key: x.key,
    stay: x.stay.map(conv),
    go: x.go.map(conv)
  }));
  const gap = s.end.gap;
  const gaps = s.scenarios.map(x => x.go[x.go.length - 1] - x.stay[x.stay.length - 1]);
  const allBetter = gaps.every(g => g >= 0);
  const allWorse = gaps.every(g => g < 0);
  const sheet = k => Object.fromEntries(Object.entries(s.sheets[k]).map(_ref => {
    let [a, v] = _ref;
    return [a, v * rate];
  }));
  return m("div", {
    className: "VStay"
  }, m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "In ", s.horizon, " years, moving to ", there, " leaves your household about ", money(Math.abs(gap)), " ", gap >= 0 ? 'better off' : 'worse off', " than staying, ", VIEW_TEXT[s.view], "."), m("div", {
    className: "VStay-toggle",
    role: "group",
    "aria-label": "Show amounts in"
  }, m("button", {
    type: "button",
    className: 'Button Button--flat' + (!ui.naira ? ' active' : ''),
    "aria-pressed": !ui.naira,
    onclick: () => ui.naira = false
  }, s.country.currency), m("button", {
    type: "button",
    className: 'Button Button--flat' + (ui.naira ? ' active' : ''),
    "aria-pressed": ui.naira,
    onclick: () => ui.naira = true
  }, "Naira")), m("div", {
    className: "VStay-pic"
  }, m.trust(wealthChart({
    scenarios: scen,
    view: s.view,
    currency: cur,
    crossover: s.crossover
  }))), m("p", {
    className: "VStay-key"
  }, m("i", {
    className: "is-go"
  }), " Move ", m("i", {
    className: "is-stay"
  }), " Stay ", m("i", {
    className: "is-band"
  }), " Range across the naira's three paths"), m("p", {
    className: "VStay-note"
  }, "What your household would own minus what it owes, in today's money.", ' ', s.crossover ? 'Moving pulls ahead in year ' + s.crossover + '. ' : gap < 0 ? 'Moving does not pull ahead within ' + s.horizon + ' years. ' : '', allBetter ? 'Moving comes out ahead on all three naira paths.' : allWorse ? 'Staying comes out ahead on all three naira paths.' : 'Which comes out ahead depends on the naira.')), m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "Under each path for the naira"), m("table", {
    className: "VStay-table"
  }, m("thead", null, m("tr", null, m("th", null, "Naira"), m("th", null, "Stay"), m("th", null, "Move"), m("th", null, "Difference"))), m("tbody", null, s.scenarios.map(x => {
    const st = x.stay[x.stay.length - 1],
      g = x.go[x.go.length - 1];
    return m("tr", {
      className: x.key === s.view ? 'is-view' : ''
    }, m("th", null, SCENARIO_LABELS[x.key], m("small", null, (x.change > 0 ? '+' : x.change < 0 ? '−' : '') + Math.abs(Math.round(x.change * 100)), "% a year")), m("td", null, money(st)), m("td", null, money(g)), m("td", {
      className: g - st >= 0 ? 'is-up' : 'is-down'
    }, (g - st >= 0 ? '+' : '−') + money(Math.abs(g - st))));
  }))), m("p", {
    className: "VStay-note"
  }, "In year ", s.horizon, ". Highlighted: the path you picked. The three paths come from how the naira's value, after inflation, has moved since 2000.")), m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "What you would own and owe in year ", s.horizon), m("div", {
    className: "VStay-pic"
  }, m.trust(balanceBars({
    sheets: {
      stay: sheet('stay'),
      go: sheet('go')
    },
    currency: cur
  }))), m("p", {
    className: "VStay-note"
  }, VIEW_TEXT[s.view].charAt(0).toUpperCase() + VIEW_TEXT[s.view].slice(1), ". Property is what it would sell for; rent from it is not counted.")), m("section", {
    className: "VAssessResult-section"
  }, m("h2", null, "What the move costs you"), m("ul", {
    className: "VStay-costs"
  }, m("li", null, m("strong", null, s.cost.move ? money(s.cost.move) : 'Nothing'), " for the move itself", s.cost.move ? s.cost.borrowed ? ', borrowed and paid back over 3 years' : ', out of your savings' : ', as your employer or school pays', "."), s.cost.sentHome ? m("li", null, m("strong", null, money(s.cost.sentHome)), " sent home over ", s.horizon, " years, in today's money.") : null, s.cost.propertySold ? m("li", null, m("strong", null, money(s.cost.propertySold)), " of property sold and moved abroad, no longer growing in Nigeria.") : null, s.cost.yearsEarningLess ? m("li", null, m("strong", null, s.cost.yearsEarningLess, " ", s.cost.yearsEarningLess === 1 ? 'year' : 'years'), " where your household takes home less abroad than it would at home, ", VIEW_TEXT[s.view], ".") : m("li", null, "Your household takes home more abroad than at home from the first year.")), s.whatIf ? m("p", {
    className: "VStay-note"
  }, "You said you save ", s.save ? 'under ' + Math.round((s.save + 0.05) * 100) + '%' : 'nothing', " of what you take home. Saving ", Math.round(s.whatIf.share * 100), "% on both paths, the difference in year ", s.horizon, " would be about", ' ', (s.whatIf.gap >= 0 ? '+' : '−') + money(Math.abs(s.whatIf.gap)), ".") : null), m("details", {
    className: "VAssessResult-section VAssessResult-answersBox"
  }, m("summary", null, "How this was worked out"), m("ul", {
    className: "VStanding-assumptions"
  }, s.assumptions.map(a => m("li", null, a)), m("li", null, "Taxes are counted in the income figures. Pensions, benefits, school fees and inheritance are not. This is general information to help you plan, not financial advice."))));
}
flarum.reg.add('voucha-assessments', 'forum/components/StayOrGoResult', { stayResult: stayResult, });